import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import AuthService, { IUserInfo } from "../services/authApi";
import apiClient from "../services/apiClient";
import { getStorage } from "../utils/storage";

const getWebSocketURL = () => {
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  const socketHost = process.env.REACT_APP_SOCKET_URL;
  return `${protocol}://${socketHost}/api/socket/ws`;
};

const WS_URL = getWebSocketURL();
console.log("WebSocket URL:", WS_URL);

export interface Message {
  from: string;
  to: string;
  header: string;
  content: string;
  timestamp: string;
}

interface WebSocketContextType {
  ws: React.MutableRefObject<WebSocket | null>;
  userInfo: IUserInfo;
  onlineUsers: string[];
  messages: Message[];
  isConnected: boolean;
  sendMessage: (to: string, header: string, content: string) => void;
  fetchMessages: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined,
);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
};

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const [userInfo, setUserInfo] = useState<IUserInfo>({} as IUserInfo);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(
    localStorage.getItem("token"),
  );

  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem("token");
      setAuthToken(token);
    };

    window.addEventListener("storage", handleStorageChange);

    const interval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (token !== authToken) {
        setAuthToken(token);
      }
    }, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [authToken]);

  useEffect(() => {
    if (!authToken) {
      setUserInfo({} as IUserInfo);
      setMessages([]);
      return;
    }

    AuthService.getUserInfoAPI()
      .then((res) => {
        setUserInfo(res.data.data.user_info);
      })
      .catch((error) => {
        console.error("Failed to get user info:", error);
        setUserInfo({} as IUserInfo);
        setMessages([]);
      });
  }, [authToken]);

  useEffect(() => {
    if (!userInfo.full_name) return;

    let shouldReconnect = true;

    const connectWebSocket = () => {
      if (!shouldReconnect) return;

      const wsUrl = `${WS_URL}?user=${encodeURIComponent(userInfo.full_name)}`;
      const socket = new window.WebSocket(wsUrl);
      ws.current = socket;

      socket.onopen = () => {
        console.log("WebSocket connected");
        setIsConnected(true);

        const token = getStorage("token");
        if (token && socket.readyState === WebSocket.OPEN) {
          socket.send(
            JSON.stringify({
              type: "auth",
              token: token,
            }),
          );
        }
      };

      socket.onmessage = (e) => {
        console.log("WebSocket message received:", e.data);

        if (e.data === "new_message") {
          fetchMessages();
          return;
        }

        try {
          const data = JSON.parse(e.data);

          if (
            data.type === "online_users_update" &&
            Array.isArray(data.users)
          ) {
            console.log("Online users updated:", data.users);
            setOnlineUsers(data.users);
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
        setIsConnected(false);
      };

      socket.onclose = () => {
        console.log("WebSocket disconnected");
        setIsConnected(false);
        if (ws.current === socket) {
          ws.current = null;
        }

        if (shouldReconnect && userInfo.full_name) {
          reconnectTimeoutRef.current = window.setTimeout(() => {
            connectWebSocket();
          }, 3000);
        }
      };
    };

    connectWebSocket();

    return () => {
      shouldReconnect = false;

      if (reconnectTimeoutRef.current !== null) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      const socket = ws.current;
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close();
        ws.current = null;
      }
    };
  }, [userInfo.full_name]);

  const fetchMessages = async () => {
    if (!userInfo.full_name) return;
    try {
      const token = getStorage("token");
      const response = await apiClient.get(
        `socket/messages?user_id=${encodeURIComponent(userInfo.full_name)}&limit=50`,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Set the token dynamically here
          },
        },
      );
      if (response.status === 200) {
        const data = response.data;
        setMessages(data || []);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  useEffect(() => {
    if (userInfo.full_name) {
      fetchMessages();
      // ไม่ต้อง fetch online users แล้ว เพราะ backend จะ push มาให้อัตโนมัติผ่าน WebSocket
    }
  }, [userInfo.full_name]);

  const sendMessage = (to: string, header: string, content: string) => {
    if (
      ws.current &&
      ws.current.readyState === WebSocket.OPEN &&
      to &&
      header &&
      content
    ) {
      ws.current.send(JSON.stringify({ to, header, content }));
      setTimeout(fetchMessages, 500);
    }
  };

  const value: WebSocketContextType = {
    ws,
    userInfo,
    onlineUsers,
    messages,
    isConnected,
    sendMessage,
    fetchMessages,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
